import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Logo, type LogoVariant } from '@/components/ui/Logo'
import {
  LOGO_ACCEPT,
  LogoRejectedError,
  LogoUploadFailedError,
  useRemoveEntityLogo,
  useUploadEntityLogo,
} from '@/features/settings/api/useEntityLogo'
import type { IssuingEntity } from '@/types/domain'

/* The issuing entity's logo, on the settings card (FR-64, migration 0041).
 *
 * ── WHAT THIS CONTROL IS FOR ──────────────────────────────────────────────────
 *
 * Every document this platform renders — offer, invoice, debit note, status
 * update — printed the entity's NAME as a wordmark where its logo belongs, for
 * every one of the three audits that recorded it. The renderer could always draw a
 * mark; there was simply no way to give it one. This is that way.
 *
 * ── WHAT IT SHOWS WHEN THERE IS NO LOGO, AND WHY THAT IS NOT AN ERROR ────────
 *
 * The wordmark. An entity with no uploaded mark is the ORDINARY state — it is what
 * every entity is in until somebody uploads one, and `docs.Branding.Missing`
 * deliberately does not demand a logo, because demanding it would refuse every
 * document in the system on the day the migration landed. So the empty state is
 * shown as what the document will actually print rather than as a gap: the
 * preview is the answer to "what will a client see", and for an entity with no
 * logo the honest answer is its name.
 *
 * That is also why `has` is read rather than the URL. An entity whose logo exists
 * but whose short-lived URL could not be signed shows the wordmark AND keeps the
 * remove control — because the mark is there, and offering "upload one" for a logo
 * that is already on every invoice would be the screen lying about the state.
 */

export function EntityLogoField({ entity }: { entity: IssuingEntity }) {
  const { t } = useTranslation()
  const upload = useUploadEntityLogo()
  const remove = useRemoveEntityLogo()

  /* A ref rather than a controlled input. A file input's value cannot be set
     programmatically (browsers forbid it, for good reason), so the only way to let
     the same file be chosen twice — which happens the moment somebody re-exports
     their artwork and picks it again — is to clear the element after each pick. */
  const picker = useRef<HTMLInputElement>(null)

  const pending = upload.isPending || remove.isPending

  /* Both mutations' errors, surfaced HERE rather than left on the object.
     `docs/llm-wiki` records that there is no global mutation error handler in this
     client, so an unread `.error` is a silent refusal — a button that appears to do
     nothing. Three refusals reach this control and each says something different:
     a type or size the client itself rejected, a bucket that refused the bytes, and
     a problem document from our own API. */
  const failure = upload.error ?? remove.error

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-surface-sunken border-line flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
        {entity.logo.has && entity.logo.url ? (
          /* The real mark, at the height the header draws it. `alt` names the
             entity rather than saying "logo": a screen reader reading "logo" on a
             page of two entity cards learns nothing about which one it is. */
          <img
            src={entity.logo.url}
            alt={entity.name}
            className="max-h-[22px] max-w-[50%] object-contain"
          />
        ) : (
          /* The wordmark, in the entity's own colourway — which is what
             logoVariant has always been for, and what it keeps doing after 0041.
             Previously hard-coded to `lockup-horizontal-blue` on every card,
             which was harmless while all eight variants collapsed onto two files
             and actively misleading the moment a real mark existed beside it. */
          <Logo variant={asLogoVariant(entity.logoVariant)} height={22} />
        )}

        <div className="flex shrink-0 gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            disabled={pending}
            onClick={() => picker.current?.click()}
          >
            {entity.logo.has
              ? t('settings.entities.logo.replace')
              : t('settings.entities.logo.upload')}
          </Button>

          {entity.logo.has ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => remove.mutate(entity.id)}
            >
              {t('settings.entities.logo.remove')}
            </Button>
          ) : null}
        </div>
      </div>

      <input
        ref={picker}
        type="file"
        accept={LOGO_ACCEPT}
        className="hidden"
        /* Named for assistive technology even though it is visually hidden and
           driven by the button above: it is still in the accessibility tree, and an
           unlabelled file input is one UX-DR-25 counts as a control without a
           label. */
        aria-label={t('settings.entities.logo.upload')}
        onChange={(e) => {
          const file = e.target.files?.[0]
          // Cleared unconditionally, so re-picking the same file fires a change
          // event. Done before the mutation rather than after, because the
          // mutation is async and the element must not still be holding the file
          // when the user reaches for it again.
          e.target.value = ''
          if (file) upload.mutate({ id: entity.id, file })
        }}
      />

      {pending ? (
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('settings.entities.logo.sending')}
        </p>
      ) : null}

      {failure ? (
        <p className="text-negative text-[12px] leading-snug font-light" role="alert">
          {t(logoFailureKey(failure))}
        </p>
      ) : null}

      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('settings.entities.logo.hint')}
      </p>
    </div>
  )
}

/** Narrows a stored variant to one the component knows, or `undefined`.
 *
 *  `logo_variant` is a `text` column with no CHECK — 0003 stores whatever the
 *  settings form sent — so the value on the wire is a `string` and the component
 *  takes a union. An unknown value falls back to the component's own default
 *  rather than being asserted through, because the alternative is a runtime lookup
 *  miss inside a `Set` and a mark that silently renders in the wrong colourway on a
 *  document. Narrowing here keeps that decision at the boundary where the string
 *  arrives, once, instead of at every render. */
function asLogoVariant(value: string): LogoVariant | undefined {
  return KNOWN_LOGO_VARIANTS.includes(value as LogoVariant)
    ? (value as LogoVariant)
    : undefined
}

/** The eight the component draws. Listed rather than derived, because a union is
 *  erased at runtime and there is nothing to iterate — and listing it means adding
 *  a ninth is a deliberate edit in both places. */
const KNOWN_LOGO_VARIANTS: readonly LogoVariant[] = [
  'lockup-horizontal-dark-text',
  'lockup-horizontal-light-text',
  'lockup-horizontal-blue',
  'lockup-horizontal-white',
  'lockup-vertical-dark-text',
  'lockup-vertical-light-text',
  'mark-blue',
  'mark-white',
]

/** Maps a refusal to its translation KEY rather than to a sentence.
 *
 *  A key, not prose, and not a `t` passed in as a parameter: the catalogue is
 *  strongly typed, so returning the key lets the compiler check that every branch
 *  names copy that actually exists in both languages — which is what the parity
 *  check then holds English and Russian to. A helper taking a loosely-typed `t`
 *  would compile with a key nobody wrote and render the key itself at a founder.
 *
 *  The two client-side classes carry a code rather than prose, deliberately (AD-27
 *  puts UI strings on this side). Everything else falls through to one generic
 *  sentence — including a problem document, because the three server refusals a
 *  founder can act on (unsupported type, too large, the upload never landed) are
 *  either already distinguished by the two branches below or are restatements of
 *  them, and a fourth sentence for the same condition is how two screens come to
 *  word one refusal differently. */
function logoFailureKey(error: Error) {
  if (error instanceof LogoRejectedError) {
    return error.reason === 'type'
      ? ('settings.entities.logo.errorType' as const)
      : ('settings.entities.logo.errorSize' as const)
  }
  if (error instanceof LogoUploadFailedError) {
    return 'settings.entities.logo.errorUpload' as const
  }
  return 'settings.entities.logo.errorGeneric' as const
}
